import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Grid,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Checkbox,
  FormGroup,
  FormControlLabel,
  useTheme,
  alpha,
  Stack,
} from '@mui/material';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import { tcgDataService } from '../../services/tcgDataService';
import { loadOnePieceSets } from '../../utils/onepieceData';
import { useNavigate } from 'react-router-dom';
import LazyImage from '../../components/LazyImage';
import { opCardSearchService } from '../../services/opCardSearchService';
import { OnePieceCard, OnePieceSet } from '../../types/onepiece';
import GameCard from '../../components/GameCard/GameCard';
import { useInfiniteQuery } from '@tanstack/react-query';

const OnePieceGlobalSearch: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter states
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedRarity, setSelectedRarity] = useState<string>('all');
  const [showChaseOnly, setShowChaseOnly] = useState(false);
  
  const [groupSearchResults, setGroupSearchResults] = useState(true);
  const [selectedCard, setSelectedCard] = useState<OnePieceCard | null>(null);

  // Debounced search term for TanStack Query
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: queryData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['opGlobalSearch', debouncedTerm, selectedColors, selectedCategory, selectedRarity, showChaseOnly],
    queryFn: ({ pageParam = 0 }) => opCardSearchService.filterCardsAdvanced({
      searchTerm: debouncedTerm,
      colors: selectedColors.length > 0 ? selectedColors : undefined,
      categories: selectedCategory !== 'all' ? [selectedCategory] : undefined,
      rarities: selectedRarity !== 'all' ? [selectedRarity] : undefined,
      chaseOnly: showChaseOnly,
      limit: 20,
      offset: pageParam
    }),
    getNextPageParam: (lastPage, allPages) => lastPage.hasMore ? allPages.length * 20 : undefined,
    enabled: debouncedTerm.length >= 2,
    staleTime: 5 * 60 * 1000,
  });

  const searchResults = useMemo(() => 
    queryData?.pages.flatMap(page => page.cards) || [], 
  [queryData]);

  const totalResults = queryData?.pages[0]?.total || 0;
  const hasMore = hasNextPage;
  const loading = isFetching;

  const [allSets, setAllSets] = useState<OnePieceSet[]>([]);

  useEffect(() => {
    tcgDataService.getOnePieceSets().then((sets) => setAllSets(loadOnePieceSets(sets)));
  }, []);

  // Available filter options
  const [availableColors, setAvailableColors] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [availableRarities, setAvailableRarities] = useState<string[]>([]);

  // Initialize search service and get filter options
  useEffect(() => {
    opCardSearchService.initialize().then(() => {
      setAvailableColors(opCardSearchService.getAvailableColors());
      setAvailableCategories(['all', ...opCardSearchService.getAvailableCategories()]);
      setAvailableRarities(['all', ...opCardSearchService.getAvailableRarities()]);
    });
  }, []);



  const handleColorToggle = (color: string) => {
    setSelectedColors(prev =>
      prev.includes(color)
        ? prev.filter(c => c !== color)
        : [...prev, color]
    );
  };

  const getColorChipColor = (color: string): string => {
    const colorMap: { [key: string]: string } = {
      Red: '#e74c3c',
      Blue: '#3498db',
      Green: '#2ecc71',
      Purple: '#9b59b6',
      Black: '#34495e',
      Yellow: '#f39c12',
    };
    return colorMap[color] || '#95a5a6';
  };

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        One Piece TCG Card Search
      </Typography>

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search cards by name or number (e.g. Luffy, OP01-001)"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
          Filters
        </Typography>
        
        <Grid container spacing={2}>
          {/* Color Filter */}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Colors:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {availableColors.map((color) => (
                <Chip
                  key={color}
                  label={color}
                  onClick={() => handleColorToggle(color)}
                  sx={{
                    backgroundColor: selectedColors.includes(color)
                      ? getColorChipColor(color)
                      : 'default',
                    color: selectedColors.includes(color) ? '#fff' : 'inherit',
                    border: `2px solid ${getColorChipColor(color)}`,
                    '&:hover': {
                      backgroundColor: alpha(getColorChipColor(color), 0.7),
                      color: '#fff',
                    },
                  }}
                />
              ))}
            </Box>
          </Grid>

          {/* Category Filter */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e: SelectChangeEvent) => setSelectedCategory(e.target.value)}
              >
                {availableCategories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Rarity Filter */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Rarity</InputLabel>
              <Select
                value={selectedRarity}
                label="Rarity"
                onChange={(e: SelectChangeEvent) => setSelectedRarity(e.target.value)}
              >
                {availableRarities.map((rarity) => (
                  <MenuItem key={rarity} value={rarity}>
                    {rarity === 'all' ? 'All Rarities' : rarity}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Chase Cards Only */}
          <Grid item xs={12}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showChaseOnly}
                    onChange={(e) => setShowChaseOnly(e.target.checked)}
                    icon={<StarIcon />}
                    checkedIcon={<StarIcon />}
                  />
                }
                label="Show Chase Cards Only"
              />
            </FormGroup>
          </Grid>
        </Grid>
      </Box>

      {/* Loading Indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'center' },
            mb: 2,
            gap: 2
          }}>
            <Chip
              label={`Search Results (${totalResults} found)`}
              color="primary"
              variant="outlined"
              sx={{
                order: { xs: 2, sm: 1 },
                fontWeight: 'bold',
                fontSize: '0.9rem',
                py: 2
              }}
            />

            <ToggleButtonGroup
              value={groupSearchResults ? 'grouped' : 'flat'}
              exclusive
              onChange={(_e, val) => {
                if (val === 'grouped') setGroupSearchResults(true);
                if (val === 'flat') setGroupSearchResults(false);
              }}
              size="small"
              fullWidth={false}
              sx={{
                order: { xs: 1, sm: 2 },
                ml: { xs: 0, sm: 'auto' },
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                '& .MuiToggleButton-root': { flex: { xs: 1, sm: 'initial' } }
              }}
            >
              <ToggleButton value="grouped">Group by Set</ToggleButton>
              <ToggleButton value="flat">Flat List</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {groupSearchResults ? (
            // Grouped by Set
            Object.entries(
              searchResults.reduce((acc, card) => {
                if (!acc[card.setId]) acc[card.setId] = [];
                acc[card.setId].push(card);
                return acc;
              }, {} as Record<string, OnePieceCard[]>)
            ).map(([setId, cards]) => {
              const set = allSets.find((s: OnePieceSet) => s.id === setId);
              if (!set) return null;
              return (
                <Box
                  key={setId}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: '1px solid',
                    borderColor: (theme) => alpha(theme.palette.text.primary, 0.1),
                    borderRadius: 3,
                    background: (theme) => alpha(theme.palette.background.paper, 0.6),
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {/* Set Header */}
                  <Box
                    sx={{
                      mb: 2,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: 'center',
                      gap: 2,
                      cursor: 'pointer',
                      textAlign: { xs: 'center', sm: 'left' }
                    }}
                    onClick={() => navigate(`/onepiece/sets/${set.id}`)}
                  >
                    <Box sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      boxShadow: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 100,
                      alignSelf: { xs: 'center', sm: 'flex-start' }
                    }}>
                      <LazyImage
                        src={set.images.logo}
                        alt={set.name}
                        width="auto"
                        placeholderHeight={40}
                        style={{
                          height: '50px',
                          maxWidth: '140px',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>

                    <Box sx={{ flex: 1, width: '100%' }}>
                      <Typography variant="h6" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                        {set.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {set.series} • {set.era} • {set.printedTotal} cards
                      </Typography>
                    </Box>
                  </Box>

                  {/* Cards Grid */}
                  <Box
                    sx={{
                      maxHeight: 350,
                      overflowY: 'auto',
                      p: 1,
                      border: '1px solid',
                      borderColor: (theme) => alpha(theme.palette.text.primary, 0.05),
                      borderRadius: 2,
                      background: (theme) => alpha(theme.palette.background.default, 0.4),
                    }}
                  >
                    <Grid container spacing={1}>
                      {(cards as OnePieceCard[]).map((card: OnePieceCard) => (
                        <Grid
                          item
                          xs={6}
                          sm={4}
                          md={3}
                          lg={2}
                          key={card.id}
                        >
                          <GameCard
                            card={card}
                            game="onepiece"
                            layout="grid"
                            onClick={() => {
                              setSelectedCard(card);
                              // TODO: Open detail dialog
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>
              );
            })
          ) : (
            // Flat List
            <Box
              sx={{
                p: 1,
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.text.primary, 0.05),
                borderRadius: 2,
                background: (theme) => alpha(theme.palette.background.paper, 0.6),
              }}
            >
              <Grid container spacing={1}>
                {searchResults.map((card: OnePieceCard) => (
                  <Grid
                    item
                    xs={6}
                    sm={4}
                    md={3}
                    lg={2}
                    key={card.id + card.setId}
                  >
                    <GameCard
                      card={card}
                      game="onepiece"
                      layout="flat"
                      onClick={() => setSelectedCard(card)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {hasMore && (
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="outlined" 
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                startIcon={isFetchingNextPage ? <CircularProgress size={20} /> : null}
              >
                {isFetchingNextPage ? 'Loading...' : 'Load More One Piece Cards'}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {!loading && searchTerm.length >= 2 && searchResults.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No cards found matching your search
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OnePieceGlobalSearch;
